import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROGRAMS, listed, schoolOf, shown } from './programs';
import { useCourseWeeks } from './about/SchoolStats';
import T54A_PHOTO from './T54A/images/t54a.webp';
import TH73A_PHOTO from './TH73A/images/th73a.webp';

// The biggest numbers of each school, under its tile: how long the course is and what its
// EPs/Limits exam asks. Counted from the data (about/platforms.js, the syllabi on the mirror),
// never typed. Weeks are grouped by value, so Delta and Echo at 28 production weeks each show
// one 28; syllabi that differ show one figure each, named. A school with no syllabus shows no
// weeks and one with no NWCs (NIFE) no NWC count.
//
// The weeks come from a school's syllabi, so they are read through `shown` rather than off the
// flag: a draft Discussion Items tab is hidden on the live site, and its syllabi are published
// to the same server as everybody else's, so testing the flag for truth would print a course
// length here for a tab nobody can open.
//
// The EPs, limits and NWCs are counted from every EPs/Limits data file, which this page, the
// only one in the entry bundle, has no other use for. So about/platforms.js is imported when
// the page mounts, into a chunk of its own, and those figures show a dash (the NWCs nothing)
// until it lands. The About pages import it the ordinary way.
const fmt = (n) => n.toLocaleString('en-US');

// One import for the session; a failed one is forgotten, so the next visit tries again.
let platformsLoad = null;
let platformsLoaded = null;
const loadPlatforms = () => {
  if (!platformsLoad) {
    platformsLoad = import('./about/platforms').then(
      (m) => { platformsLoaded = m.PLATFORMS; return m.PLATFORMS; },
      (err) => { platformsLoad = null; throw err; },
    );
  }
  return platformsLoad;
};

// "P-8, E-6, C-130, USCG, Tilt-Rotor" — plain commas. These are the communities flying one
// syllabus, not a sentence, and the "and" only lengthened a label that was already the widest
// thing under the tile.
const listOf = (names) => names.join(', ');

// `platforms` is this school's rows of about/platforms.js, or null while that is loading.
function SchoolHighlights({ id, platforms }) {
  const program = PROGRAMS.find((p) => p.id === id);
  const hasSyllabi = shown(program.discuss);
  const weeks = useCourseWeeks(schoolOf(program), hasSyllabi);
  const sum = (k) => (platforms
    ? platforms.reduce((n, p) => n + (k === 'limits' ? p.limits : p.eps[k] || 0), 0)
    : null);

  // A school whose syllabi run to different lengths names the communities each one covers, and
  // those names are long enough to shoulder the exam figures out of line. So they get a row of
  // their own, reading across the top with the EPs, limits and NWCs lined up beneath. On that
  // row the unit is said ONCE, at the left, and each figure sits over its own communities with
  // a rule between them: "weeks" on every group, spelled out with "and", ran wider than the
  // tile. One syllabus keeps its short label and its place in the row below.
  const named = !!weeks && weeks.length > 1;
  const lengths = [];
  if (hasSyllabi && !named) {
    // An empty list is a school whose syllabi carry no course length (or aren't published yet).
    lengths.push(weeks && weeks.length
      ? { key: `weeks-${weeks[0].weeks}`, value: weeks[0].weeks, label: weeks[0].label.toLowerCase() }
      : { key: 'weeks', value: null, label: 'weeks' });
  }
  const figures = [];
  figures.push({ key: 'eps', value: sum('eps'), label: 'EPs' });
  figures.push({ key: 'limits', value: sum('limits'), label: 'limits' });
  if (sum('nwcs')) figures.push({ key: 'nwcs', value: sum('nwcs'), label: 'NWCs' });

  const stat = (f) => (
    <div key={f.key} className="landing-stat">
      <dt>{f.label}</dt>
      <dd>{f.value == null ? '–' : fmt(f.value)}</dd>
    </div>
  );

  // Two lists rather than one with a spacer in it: a `dl` holds groups of dt and dd, and a
  // wrapper div that holds neither is not one of them. The unit word sits outside its `dl` for
  // the same reason — it belongs to every group on the row, so it is not a term of any of them.
  return (
    <div className="landing-stats-stack">
      {named && (
        <div className="landing-weeks">
          <span className="landing-weeks-unit">weeks,</span>
          <dl className="landing-stats landing-weeks-list">
            {weeks.map((w) => (
              <div key={w.weeks} className="landing-stat">
                <dt>{listOf(w.names)}</dt>
                <dd>{fmt(w.weeks)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      <dl className="landing-stats">
        {!named && lengths.map(stat)}
        {figures.map(stat)}
      </dl>
    </div>
  );
}

// The school tiles, in the order they fill the rows. The Advanced schools are named for the
// airframe, not just the stage: Advanced covers several schools.
const TILES = [
  { id: 'nife', name: 'NIFE', href: '/nife/about', img: '/images/c172.webp', alt: 'NIFE - Cessna 172' },
  { id: 'tw4', name: 'Primary', href: '/primary/about', img: '/images/t6b.webp', alt: 'Primary - T-6B Texan II' },
  { id: 'th73a', name: 'TH-73A Advanced', href: '/th73a/about', img: TH73A_PHOTO, alt: 'TH-73A Advanced - Thrasher' },
  { id: 't44c', name: 'T-44C Advanced', href: '/t44c/about', img: '/images/t44c.webp', alt: 'T-44C Advanced - Pegasus' },
  // A photo whose aircraft is wider than the tile: keep the nose.
  { id: 't54a', name: 'T-54A Advanced', href: '/t54a/about', img: T54A_PHOTO, alt: 'T-54A Advanced - King Air', imgClass: 'landing-photo--nose' },
];

const rowsOf = (list, n) => Array.from({ length: Math.ceil(list.length / n) }, (_, i) => list.slice(i * n, i * n + n));

function LandingPage() {
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState(platformsLoaded);
  useEffect(() => {
    let live = true;
    loadPlatforms().then((p) => { if (live) setPlatforms(p); }, () => {});
    return () => { live = false; };
  }, []);
  // Preload images to prevent mobile display issues
  useEffect(() => {
    const imagesToPreload = [
      '/images/c172.webp',
      '/images/t6b.webp',
      '/images/t44c.webp'
    ];

    imagesToPreload.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return (
    <div className="landing-page">
      <div className="landing-header">
        <h1>Welcome to <span className="pink-text">pinksheetmafia.com</span></h1>
        <p className="landing-explainer">
          This is a free, community-built, open-source resource designed to make naval aviation training less stressful.
          Select your school below to access study materials, interactive cockpits, checklists, and more.
        </p>
      </div>

      {/* Rows of three, each centred, filled in this order. Programs not yet listed are left
          out before the split, so the rows close up rather than leave a hole. */}
      <div className="landing-buttons">
        {rowsOf(TILES.filter((t) => listed(PROGRAMS.find((p) => p.id === t.id))), 3).map((row) => (
          <div key={row[0].id} className="landing-row">
            {row.map((t) => (
              <div key={t.id} className="landing-school">
                <div
                  className="landing-button"
                  onClick={() => navigate(t.href)}
                >
                  <div className="landing-button-fallback">{t.name}</div>
                  <img src={t.img} alt={t.alt} className={t.imgClass} />
                  <div className="landing-button-label">{t.name}</div>
                </div>
                <SchoolHighlights id={t.id} platforms={platforms && platforms[t.id]} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default LandingPage;